import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody, CardHeader } from '@structural-climbing/ui'
import type { DaySummary } from './types'
import { addMonths, monthGrid, monthTitle, weekdayNames } from './weekRange'

interface Props {
  /** Any day of the year on show. */
  year: string
  today: string
  days: Record<string, DaySummary>
  events: Record<string, number>
  onOpenDay: (day: string) => void
  onOpenMonth: (day: string) => void
}

/** Tailwind needs the full class names in the source to keep them. */
function shade(classes: number): string {
  if (classes === 0) return ''
  if (classes === 1) return 'bg-primary/20'
  if (classes <= 3) return 'bg-primary/45'
  return 'bg-primary/75 text-primary-foreground'
}

/**
 * The year at a glance: twelve small months, each day shaded by how many
 * classes it holds and dotted when the gym has an event. Counts come from the
 * summary endpoint, which creates nothing, so a year costs one light request.
 */
export function YearView({ year, today, days, events, onOpenDay, onOpenMonth }: Props) {
  const { t, i18n } = useTranslation()
  const names = useMemo(
    () => weekdayNames(i18n.language).map((n) => n.slice(0, 2)),
    [i18n.language],
  )
  const summaryLabel = useCallback(
    (classes: number, booked: number, capacity: number) =>
      `${t('calendar.classesCount', { count: classes })} · ${t('calendar.placesCount', { booked, capacity })}`,
    [t],
  )
  const dayLabel = useCallback(
    (day: string, s: DaySummary | undefined, ev: number) =>
      [
        day,
        t('calendar.classesCount', { count: s?.classes ?? 0 }),
        s ? t('calendar.placesCount', { booked: s.booked, capacity: s.capacity }) : null,
        ev ? t('calendar.eventsCount', { count: ev }) : null,
      ]
        .filter(Boolean)
        .join(' · '),
    [t],
  )
  const months = useMemo(() => {
    const first = `${year.slice(0, 4)}-01-01`
    return Array.from({ length: 12 }, (_, i) => addMonths(first, i))
  }, [year])

  return (
    <div className="flex flex-col gap-3" data-testid="year-view">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {months.map((month) => (
          <MiniMonth
            key={month}
            month={month}
            today={today}
            days={days}
            events={events}
            names={names}
            title={monthTitle(month, i18n.language)}
            onOpenDay={onOpenDay}
            onOpenMonth={onOpenMonth}
            summaryLabel={summaryLabel}
            dayLabel={dayLabel}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-foreground/60">
        <p>{t('calendar.legendFewer')}</p>
        {[1, 2, 4].map((n) => (
          <div key={n} className={`h-3 w-3 rounded-sm border border-divider ${shade(n)}`} />
        ))}
        <p>{t('calendar.legendMore')}</p>
        <div className="ml-3 h-1.5 w-1.5 rounded-full bg-secondary" />
        <p>{t('calendar.legendEvent')}</p>
      </div>
    </div>
  )
}

interface MiniMonthProps {
  month: string
  today: string
  days: Record<string, DaySummary>
  events: Record<string, number>
  names: string[]
  title: string
  onOpenDay: (day: string) => void
  onOpenMonth: (day: string) => void
  summaryLabel: (classes: number, booked: number, capacity: number) => string
  dayLabel: (day: string, summary: DaySummary | undefined, events: number) => string
}

const MiniMonth = memo(function MiniMonth({
  month,
  today,
  days,
  events,
  names,
  title,
  onOpenDay,
  onOpenMonth,
  summaryLabel,
  dayLabel,
}: MiniMonthProps) {
  const monthKey = month.slice(0, 7)
  const grid = useMemo(() => monthGrid(month), [month])
  const totals = useMemo(() => {
    let classes = 0
    let booked = 0
    let capacity = 0
    for (const [day, s] of Object.entries(days)) {
      if (!day.startsWith(monthKey)) continue
      classes += s.classes
      booked += s.booked
      capacity += s.capacity
    }
    return { classes, booked, capacity }
  }, [days, monthKey])

  return (
    <Card shadow="none" className="border border-divider">
      <CardHeader className="flex flex-col items-start gap-0.5 pb-1">
        <Button
          variant="light"
          size="sm"
          className="h-auto min-w-0 px-1 py-0.5 text-sm font-semibold capitalize"
          onPress={() => onOpenMonth(month)}
        >
          {title}
        </Button>
        <p className="px-1 text-xs text-foreground/50">
          {summaryLabel(totals.classes, totals.booked, totals.capacity)}
        </p>
      </CardHeader>
      <CardBody className="pt-1">
        <div className="grid grid-cols-7 gap-0.5">
          {names.map((n) => (
            <p key={n} className="text-center text-[10px] uppercase text-foreground/40">
              {n}
            </p>
          ))}
          {grid.map((day) => {
            if (!day.startsWith(monthKey)) return <div key={day} />
            const s = days[day]
            const ev = events[day] ?? 0
            const isToday = day === today
            return (
              <Button
                key={day}
                isIconOnly
                size="sm"
                variant="light"
                radius="sm"
                disableRipple
                disableAnimation
                aria-label={dayLabel(day, s, ev)}
                title={dayLabel(day, s, ev)}
                className={`relative h-7 w-full min-w-0 text-[11px] tabular-nums ${shade(s?.classes ?? 0)} ${
                  isToday ? 'ring-2 ring-primary' : ''
                }`}
                onPress={() => onOpenDay(day)}
              >
                {Number(day.slice(8))}
                {ev > 0 && (
                  <div className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-secondary" />
                )}
              </Button>
            )
          })}
        </div>
      </CardBody>
    </Card>
  )
})
